import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import axios from 'axios';
import { Form, FormSpy } from 'react-final-form';
import { Column, Button, Text, Flex } from '../../design-system';
import styled, { css } from 'styled-components';
import Alert from '../../helpers/alert';
import { ERR_MSG } from '../../globalConstants';
import FieldColumn from '../../components/FieldColumn';
import { required, composeValidators, passwordVal } from '../../helpers/utils';
import GrantholsterBrand, {
  MainContainer,
  FormContainer,
  StyledBlockLink,
  SuccessfulSubmission,
} from '../../components/GrantholsterBrand';

const StyledButton = styled(Button)(
  ({ theme }) => css`
    font-weight: 400;
    color: ${theme.colors.grey};
    font-size: 0.9rem;
    padding: 0;
  `,
);

const GrayStyledBlockLink = styled(StyledBlockLink)(
  ({ theme }) => css`
    font-weight: 400;
    color: ${theme.colors.grey};
    font-size: 0.9rem;
  `,
);

const ResetPassword = () => {
  const location = useLocation();
  const [email, setEmail] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [hasPasswordBeenReset, setHasPasswordBeenReset] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    setToken(urlParams.get('token'));
    setEmail(urlParams.get('email'));
  }, []);

  const onSubmit = async (values: Record<string, string>) => {
    try {
      const response = await axios.post('/auth/password/reset', {
        ...values,
        email,
        token,
      });
      if (response.status === 200) {
        setHasPasswordBeenReset(true);
        Alert.success(response.data.message);
      }
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 400) {
        Alert.error('Failed to reset password, please try another reset link!');
      } else {
        Alert.error(ERR_MSG);
      }
    }
  };

  const onSendLinkAgain = async () => {
    try {
      const response = await axios.post('/auth/password/email', { email });
      if (response.status === 200) {
        Alert.success('Please check your email for a new reset link');
      }
    } catch (error: any) {
      Alert.error(ERR_MSG);
    }
  };

  return (
    <MainContainer fluid>
      <Flex height="100%">
        <Column
          flexDirection="column"
          width="50%"
          justifyContent="center"
          alignItems="center"
        >
          {hasPasswordBeenReset ? (
            <>
              <SuccessfulSubmission alignItems="center">
                <Text>Your password has been successfully reset</Text>
                <StyledBlockLink to="/login" color="blue">
                  Login
                </StyledBlockLink>
              </SuccessfulSubmission>
            </>
          ) : (
            <>
              <Text mb={20} fontSize={20}>
                Reset Password
              </Text>
              <FormContainer>
                <Column>
                  <Form
                    onSubmit={onSubmit}
                    validate={(values: Record<string, string>) => {
                      const errors: Record<string, string> = {};
                      if (values.password !== values.password_confirmation) {
                        errors.password_confirmation =
                          'Passwords do not match.';
                      }
                      return errors;
                    }}
                    render={({ handleSubmit, submitting, pristine }) => (
                      <form onSubmit={handleSubmit}>
                        <FormSpy
                          subscription={{ valid: true }}
                          onChange={(state: { valid: boolean }) => {
                            const { valid } = state;
                            setIsDisabled(!valid);
                          }}
                        />
                        <Flex>
                          <FieldColumn
                            name="password"
                            label="New Password"
                            type="password"
                            placeholder="New Password"
                            validate={composeValidators(required, passwordVal)}
                          />
                        </Flex>
                        <FieldColumn
                          name="password_confirmation"
                          label="Password Confirmation"
                          type="password"
                          placeholder="New Password Confirmation"
                        />
                        <Column mt={1} flexDirection="column">
                          <Button
                            type="submit"
                            disabled={submitting || pristine || isDisabled}
                          >
                            Reset Password
                          </Button>
                        </Column>
                      </form>
                    )}
                  />
                  <Flex justifyContent="space-between" mr={2}>
                    <GrayStyledBlockLink to="/login">
                      Log In
                    </GrayStyledBlockLink>
                    <StyledButton variant="greyText" onClick={onSendLinkAgain}>
                      Send another Link
                    </StyledButton>
                  </Flex>
                </Column>
              </FormContainer>
            </>
          )}
        </Column>

        <GrantholsterBrand />
      </Flex>
    </MainContainer>
  );
};

export default ResetPassword;
